import style from './selectBaseCalculo.module.scss';
import { useQuery } from '@tanstack/react-query';
import { useBackEndRequests } from '../../../../../../../../../lib/backEndRequests';
import { useEffect, useState } from 'react';
import { SelectOptions } from '@govbr-ds/react-components/dist/components/BrSelect';
import {
  IListaBaseCalculo,
  IRetornoListagemBaseCalculo,
  PaginasOutrosRegimes,
} from '../../../../../../../../../../../interfaces/IDadosOutrosRegimes';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { BrButton, BrLoading, BrSelect } from '@govbr-ds/react-components';
import { useNotificacao } from '../../../../../../../../../../../hooks/useNotificacao';
import { Col, Row } from 'react-dsgov';

interface IProps {
  idOperacao: string;
  baseCalculoCadastradas?: IListaBaseCalculo;
  entrarModoEdicao: (baseCalculo: PaginasOutrosRegimes) => void;
}

const fatosGeradoresOptions: SelectOptions[] = [
  { label: 'Primeira Saída por Venda', value: 'PRIMEIRA_SAIDA_POR_VENDA' },
  { label: 'Consumo', value: 'CONSUMO' },
];

const SelectBaseCalculo = ({ idOperacao, baseCalculoCadastradas, entrarModoEdicao }: IProps) => {
  const SEPARADOR_CHAVE = '|';

  const LABEL_ERRO = 'Não foi possível recuperar a lista de base de cálculo';

  const MSG_SEM_SELECAO = 'Selecione um tipo de base de cálculo para adicionar';
  const MSG_DESABILITADA =
    'Base de cálculo já cadastrada na operação, caso seja necessário incluir outras informações, edite a existente';

  const mostrarNotificacao = useNotificacao();
  const { getListaFormacaoBaseCalculo } = useBackEndRequests();

  const [fatoGeradorSelecionado, setFatoGeradorSelecionado] = useState<string>('');

  const [optionsBaseCalculo, setOptionsBaseCalculo] = useState<SelectOptions[]>([]);
  const [baseCalculoSelecionada, setBaseCalculoSelecionada] = useState<string>('');
  const [chaveBaseCalculo, setChaveBaseCalculo] = useState<string>('');

  const {
    data: requestBaseCalculo,
    error,
    isFetching,
    refetch: refetchBaseCalculo,
  } = useQuery<IRetornoListagemBaseCalculo[], IErrorWithSentryId>({
    queryKey: ['getListaFormacaoBaseCalculo', idOperacao],
    queryFn: () => getListaFormacaoBaseCalculo(idOperacao),
    retry: true,
    refetchOnWindowFocus: false,
    enabled: idOperacao !== '',
    staleTime: 0,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (requestBaseCalculo && fatoGeradorSelecionado) {
      const options = requestBaseCalculo
        .filter((base) => base.tipoFatoGerador == fatoGeradorSelecionado)
        .map((base, index) => ({
          label: base.rotulo,
          value: `${index}${SEPARADOR_CHAVE}${base.nome}`,
        }));

      setOptionsBaseCalculo(options);
    } else {
      setOptionsBaseCalculo([]);
    }
  }, [requestBaseCalculo, fatoGeradorSelecionado]);

  useEffect(() => {
    if (baseCalculoSelecionada) {
      const chaveBaseCalculo = baseCalculoSelecionada.split(SEPARADOR_CHAVE)[1];

      setChaveBaseCalculo(chaveBaseCalculo);
    }
  }, [baseCalculoSelecionada]);

  useEffect(() => {
    refetchBaseCalculo();
  }, [baseCalculoCadastradas]);

  const verificarBaseCalculoDesabilitada = () => {
    const baseCalculo = requestBaseCalculo?.find((base) => base.nome === chaveBaseCalculo);

    if (baseCalculo) {
      return baseCalculo.isHabilitado === false;
    } else {
      return true;
    }
  };

  const validarBaseCalculo = () => {
    if (baseCalculoSelecionada == '') {
      return MSG_SEM_SELECAO;
    }

    if (verificarBaseCalculoDesabilitada()) {
      return MSG_DESABILITADA;
    }

    return '';
  };

  const onClickAdicionar = () => {
    const msgValidacao = validarBaseCalculo();

    if (msgValidacao !== '') {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: msgValidacao,
      });
    } else {
      entrarModoEdicao(chaveBaseCalculo as PaginasOutrosRegimes);
    }
  };

  return (
    <div className={style.selectBaseCalculo} data-testid='select-container'>
      {isFetching && (
        <div className={style.loadingContainer} data-testid='loading-container'>
          <BrLoading large />
        </div>
      )}
      {!isFetching && !!error && (
        <div className={style.errorContainer} data-testid='error-container'>
          <span className={style.errorText}>{LABEL_ERRO}</span>
          <BrButton data-testid='btn-refetch' size='small' onClick={() => refetchBaseCalculo()}>
            (Tentar novamente)
          </BrButton>
        </div>
      )}
      {!isFetching && !error && (
        <Row className={style.lista}>
          <Col className={style.fatoGeradorSelect}>
            <BrSelect
              id='fato-gerador'
              label='Fato Gerador'
              placeholder='Selecione o fato gerador...'
              options={fatosGeradoresOptions}
              value={fatoGeradorSelecionado}
              key={fatoGeradorSelecionado}
              onChange={(value: string) => {
                setFatoGeradorSelecionado(value);
                setBaseCalculoSelecionada('');
              }}
            />
          </Col>
          <Col className={style.formacaoBaseCalculoSelect} auto xl>
            <BrSelect
              id='base-calculo'
              label='Formação de Base de Cálculo'
              placeholder='Selecione a formação de base de cálculo...'
              options={optionsBaseCalculo}
              value={baseCalculoSelecionada}
              key={baseCalculoSelecionada}
              onChange={(value: string) => setBaseCalculoSelecionada(value)}
              disabled={fatoGeradorSelecionado === ''}
            />
          </Col>
          <Col className={style.botao} auto>
            <BrButton data-testid='btn-adicionar' secondary onClick={() => onClickAdicionar()} size='small'>
              <i className={`fas fa-plus`} /> Adicionar nova base de cálculo
            </BrButton>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default SelectBaseCalculo;
